import React, { useState } from 'react';
import { PoDetail } from './poSlice';
import { uploadPoDocumentApi } from '../../services/api';
import toast from 'react-hot-toast';

interface AddDocumentModalProps {
  po: PoDetail | null;
  isOpen: boolean;
  onClose: () => void;
  onDocumentUploaded: (updatedPo: PoDetail) => void;
}

type DocumentType = 'quotation' | 'invoice' | 'delivery' | 'jobcard' | 'ticket' | 'other';

const AddDocumentModal: React.FC<AddDocumentModalProps> = ({ po, isOpen, onClose, onDocumentUploaded }) => {
  const [documentType, setDocumentType] = useState<DocumentType | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [date, setDate] = useState('');
  const [amount, setAmount] = useState('');
  const [vat, setVat] = useState<'' | '0' | '1'>('');
  const [deliveryNumber, setDeliveryNumber] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const [jobNumber, setJobNumber] = useState('');
  const [comment, setComment] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!po || !file || !documentType) {
      toast.error('Please select a document type and file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('type', documentType);
    formData.append('file', file);
    
    if (title) formData.append('title', title);
    if (description) formData.append('description', description);
    if (invoiceNumber) formData.append('invoice_number', invoiceNumber);
    if (date) formData.append('date', date);
    if (amount) formData.append('amount', amount);
    if (vat) formData.append('vat', vat);
    if (deliveryNumber) formData.append('delivery_number', deliveryNumber);
    if (receivedBy) formData.append('received_by', receivedBy);
    if (jobNumber) formData.append('job_number', jobNumber);
    if (comment) formData.append('comment', comment);

    try {
      const response = await uploadPoDocumentApi(po.id, formData);
      toast.success('Document uploaded successfully');
      onDocumentUploaded(response.data);
      handleClose();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setDocumentType('');
    setFile(null);
    setTitle('');
    setDescription('');
    setInvoiceNumber('');
    setDate('');
    setAmount('');
    setVat('');
    setDeliveryNumber('');
    setReceivedBy('');
    setJobNumber('');
    setComment('');
    onClose();
  };

  if (!isOpen || !po) return null;

  const getDocumentCount = (type: DocumentType): number => {
    switch (type) {
      case 'quotation': return po.quotation ? 1 : 0;
      case 'invoice': return po.invoices?.length || 0;
      case 'delivery': return po.deliverynotes?.length || 0;
      case 'jobcard': return po.jobcards?.length || 0;
      case 'ticket': return po.tickets?.length || 0;
      case 'other': return po.others?.length || 0;
      default: return 0;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Attach Document - {po.stores?.[0]?.name || 'N/A'}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Document Type */}
          <div>
            <label htmlFor="doctype" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Type:
            </label>
            <select
              id="doctype"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value as DocumentType)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
              required
            >
              <option value="">-- Select Type --</option>
              <option 
                value="quotation" 
                disabled={po.quotation ? true : false}
              >
                Quotation ({getDocumentCount('quotation')}){po.quotation ? ' - Already attached' : ''}
              </option>
              <option value="invoice">Invoice ({getDocumentCount('invoice')})</option>
              <option value="delivery">Delivery Note ({getDocumentCount('delivery')})</option>
              <option value="jobcard">JobCard ({getDocumentCount('jobcard')})</option>
              <option value="ticket">Colefax Ticket ({getDocumentCount('ticket')})</option>
              <option value="other">Other ({getDocumentCount('other')})</option>
            </select>
            {po.quotation && documentType === 'quotation' && (
              <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">
                A quotation is already attached to this PO. Only one quotation is allowed.
              </p>
            )}
          </div>

          {/* File Upload */}
          <div>
            <label htmlFor="file" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              File:
            </label>
            <input
              id="file"
              type="file"
              onChange={handleFileChange}
              accept={documentType === 'jobcard' || documentType === 'delivery' || documentType === 'ticket' ? 'application/pdf' : '*'}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
              required
            />
          </div>

          {/* Conditional Fields based on document type */}
          {documentType === 'quotation' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="quotation_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Quotation Number:
                </label>
                <input
                  id="quotation_number"
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date:
                </label>
                <input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Amount:
                </label>
                <input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="vat" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  VAT Type:
                </label>
                <select
                  id="vat"
                  value={vat}
                  onChange={(e) => setVat(e.target.value as '' | '0' | '1')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                >
                  <option value="">Select...</option>
                  <option value="1">Included</option>
                  <option value="0">Excluded</option>
                </select>
              </div>
            </div>
          )}

          {documentType === 'invoice' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="invoice_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Invoice Number:
                </label>
                <input
                  id="invoice_number"
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date:
                </label>
                <input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Amount:
                </label>
                <input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="vat" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  VAT Type:
                </label>
                <select
                  id="vat"
                  value={vat}
                  onChange={(e) => setVat(e.target.value as '' | '0' | '1')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                >
                  <option value="">Select...</option>
                  <option value="1">Included</option>
                  <option value="0">Excluded</option>
                </select>
              </div>
            </div>
          )}

          {documentType === 'delivery' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="delivery_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Delivery Number:
                </label>
                <input
                  id="delivery_number"
                  type="text"
                  value={deliveryNumber}
                  onChange={(e) => setDeliveryNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date:
                </label>
                <input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="received_by" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Received By:
                </label>
                <input
                  id="received_by"
                  type="text"
                  value={receivedBy}
                  onChange={(e) => setReceivedBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          {documentType === 'jobcard' && (
            <div>
              <label htmlFor="job_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Job Number:
              </label>
              <input
                id="job_number"
                type="text"
                value={jobNumber}
                onChange={(e) => setJobNumber(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
              />
            </div>
          )}

          {documentType === 'ticket' && (
            <div>
              <label htmlFor="comment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Comment:
              </label>
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                rows={3}
              />
            </div>
          )}

          {documentType === 'other' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title:
                </label>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description:
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDocumentModal;
